// Search Performance panel — "is our SEO working" view.
// Stacked VERTICAL bar chart of keyword rank-band distribution over time:
// how many tracked keywords sit in each rank band per period, and how that
// mix shifts (bands moving up = SEO working). AA report-page parity ("Rank
// Tracker - Keywords", ALIBI Music, Jul 2026): daily columns, 5-band legend
// 1-3/4-10/11-20/21-50/51+. Self-contained: only depends on recharts and the
// app's named theme tokens (no hardcoded hex, per project convention).
//
// Band colour ramp is an ORDINAL job (dataviz skill), not categorical: rank
// bands are a position-in-a-sequence, so they take ONE hue (azure) in
// monotone lightness steps rather than 5 unrelated categorical hues. The
// 5-step ramp (`--chart-rank-band-1..5`, index.css) was validated with the
// skill's `validate_palette.js --ordinal` for both light and dark surfaces
// (monotone L, adjacent steps >=0.06 apart, light end clears 2:1 contrast,
// single hue) -- its own steps per mode, not an automatic dark flip.

import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

// `band_21_50`/`band_51_plus` are OPTIONAL: today's live schema
// (RankBandPoint in schemas/client_analytics.py + its TS mirror) only ships
// the merged `band_21_plus` -- the raw AA pull already computes the true
// 5-way split (aa_rest_shapes.py's `_RANK_BAND_LABEL_ALIASES`), it just
// hasn't been threaded through the mapper/schema yet (flagged to
// rank-backend). Until it lands, this chart falls back to a 4-segment
// "21+" bar rather than blocking on that schema change.
export interface RankBandPoint {
  period: string;
  band_1_3: number;
  band_4_10: number;
  band_11_20: number;
  band_21_plus: number;
  band_21_50?: number;
  band_51_plus?: number;
}

export interface RankBandDistributionChartProps {
  data: RankBandPoint[];
}

// "2026-07-10" -> "10 Jul". Falls back to the raw label for non-date
// periods (e.g. the monthly "May"/"Jun" demo data below) rather than
// rendering "Invalid Date" -- deliberately duplicated from primitives.tsx's
// formatDayTick rather than imported, matching this file's own
// self-contained convention (see header comment).
function formatDayTick(period: string): string {
  const d = new Date(`${period}T00:00:00`);
  if (Number.isNaN(d.getTime())) return period;
  return d.toLocaleDateString("en-US", { day: "numeric", month: "short" });
}

const CHART_HEIGHT = 220;

// Best-rank band gets the darkest/strongest step of the ramp; each band
// down steps lighter -- see the ordinal-ramp note in the file header.
const FULL_BAND_SERIES: Array<{
  dataKey: "band_1_3" | "band_4_10" | "band_11_20" | "band_21_50" | "band_51_plus";
  name: string;
  color: string;
}> = [
  { dataKey: "band_1_3", name: "1-3", color: "var(--color-chart-rank-band-1)" },
  { dataKey: "band_4_10", name: "4-10", color: "var(--color-chart-rank-band-2)" },
  { dataKey: "band_11_20", name: "11-20", color: "var(--color-chart-rank-band-3)" },
  { dataKey: "band_21_50", name: "21-50", color: "var(--color-chart-rank-band-4)" },
  { dataKey: "band_51_plus", name: "51+", color: "var(--color-chart-rank-band-5)" },
];

// 4-segment fallback for today's live data shape (band_21_plus only) --
// same ramp, skipping the two middle-light steps so the merged "21+" bar
// still reads as "worse than 11-20" without inventing a fake split.
const FALLBACK_BAND_SERIES: Array<{
  dataKey: "band_1_3" | "band_4_10" | "band_11_20" | "band_21_plus";
  name: string;
  color: string;
}> = [
  { dataKey: "band_1_3", name: "1-3", color: "var(--color-chart-rank-band-1)" },
  { dataKey: "band_4_10", name: "4-10", color: "var(--color-chart-rank-band-2)" },
  { dataKey: "band_11_20", name: "11-20", color: "var(--color-chart-rank-band-3)" },
  { dataKey: "band_21_plus", name: "21+", color: "var(--color-chart-rank-band-5)" },
];

// AA "Google Rankings" KPI tile (e.g. 7) -- the SUM of the most recent
// day's rank bands. Confirmed live (rank-backend, 2026-08-09): reproduces
// AA's tile value exactly for ALIBI Music, cross-checked independently
// against RankingsSection.keywords filtered to a nonzero rank. Uses
// `band_21_plus` alone (never band_21_50+band_51_plus on top of it, even
// once both are present) since real payloads keep `band_21_plus` populated
// as the authoritative merged total alongside any future split -- see
// demoRankBands' doc comment. Fully derivable client-side; no backend field
// needed for this tile. null when there's no history to read a "most
// recent day" from, never a fabricated 0.
export function sumLatestRankBand(data: RankBandPoint[] | null | undefined): number | null {
  if (!data || data.length === 0) return null;
  const last = data[data.length - 1];
  return last.band_1_3 + last.band_4_10 + last.band_11_20 + last.band_21_plus;
}

export function RankBandDistributionChart({ data }: RankBandDistributionChartProps) {
  if (!data || data.length < 2) {
    return (
      <div
        className="flex items-center justify-center text-dash-context text-muted-foreground"
        style={{ height: CHART_HEIGHT }}
      >
        Not enough history yet
      </div>
    );
  }

  // Real 5-way split only if EVERY point carries it -- a mix of split and
  // merged points would render some bars with a phantom 21-50/51+ seam and
  // others without, which is worse than a consistent 4-segment fallback.
  const hasFullSplit = data.every(
    (p) => p.band_21_50 !== undefined && p.band_51_plus !== undefined,
  );
  const series = hasFullSplit ? FULL_BAND_SERIES : FALLBACK_BAND_SERIES;
  const tickInterval = Math.max(0, Math.ceil(data.length / 10) - 1);

  return (
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
        <XAxis
          dataKey="period"
          tickFormatter={formatDayTick}
          interval={tickInterval}
          tick={{ fontSize: 12, fill: "var(--color-muted-foreground)" }}
        />
        <YAxis
          tick={{ fontSize: 12, fill: "var(--color-muted-foreground)" }}
          allowDecimals={false}
        />
        <Tooltip
          labelFormatter={(label) => formatDayTick(String(label))}
          contentStyle={{
            borderRadius: 8,
            border: "1px solid var(--color-border)",
            fontSize: 12,
          }}
          formatter={(value, name) => [
            <span className="tabular" key="value">
              {String(value)}
            </span>,
            name,
          ]}
        />
        <Legend wrapperStyle={{ fontSize: 12, paddingTop: 6 }} iconType="circle" />
        {series.map((band, i) => (
          <Bar
            key={band.dataKey}
            dataKey={band.dataKey}
            name={band.name}
            stackId="ranks"
            fill={band.color}
            radius={i === series.length - 1 ? [2, 2, 0, 0] : undefined}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// `band_21_plus` stays populated (= band_21_50 + band_51_plus) alongside the
// split fields on every point -- real backend payloads are expected to keep
// sending it too once the split lands, matching how rank-backend shipped
// `keyword_id`/`total_rows` as ADDITIVE nullable fields rather than replacing
// anything (see MultiEngineRankRow's doc comment), not a breaking swap.
export const demoRankBands: RankBandPoint[] = [
  { period: "2026-07-02", band_1_3: 4, band_4_10: 2, band_11_20: 1, band_21_plus: 0, band_21_50: 0, band_51_plus: 0 },
  { period: "2026-07-03", band_1_3: 4, band_4_10: 2, band_11_20: 1, band_21_plus: 0, band_21_50: 0, band_51_plus: 0 },
  { period: "2026-07-04", band_1_3: 4, band_4_10: 2, band_11_20: 1, band_21_plus: 0, band_21_50: 0, band_51_plus: 0 },
  { period: "2026-07-08", band_1_3: 3, band_4_10: 2, band_11_20: 1, band_21_plus: 0, band_21_50: 0, band_51_plus: 0 },
  { period: "2026-07-09", band_1_3: 3, band_4_10: 2, band_11_20: 1, band_21_plus: 0, band_21_50: 0, band_51_plus: 0 },
  { period: "2026-07-14", band_1_3: 3, band_4_10: 2, band_11_20: 2, band_21_plus: 0, band_21_50: 0, band_51_plus: 0 },
  { period: "2026-07-18", band_1_3: 4, band_4_10: 2, band_11_20: 1, band_21_plus: 1, band_21_50: 1, band_51_plus: 0 },
  { period: "2026-07-24", band_1_3: 4, band_4_10: 2, band_11_20: 1, band_21_plus: 1, band_21_50: 1, band_51_plus: 0 },
  { period: "2026-07-28", band_1_3: 4, band_4_10: 3, band_11_20: 1, band_21_plus: 0, band_21_50: 0, band_51_plus: 0 },
  { period: "2026-07-30", band_1_3: 4, band_4_10: 3, band_11_20: 1, band_21_plus: 0, band_21_50: 0, band_51_plus: 0 },
];
